import React, { useEffect, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { FileText } from "lucide-react";
import { mediaSrc } from "../api";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

// media.id -> dataURL cache: scrolling/reopening never re-renders the PDF
const thumbCache = new Map();

const looksLikePdf = (media) =>
  (media?.content_type || "").toLowerCase().includes("pdf") ||
  /\.pdf$/i.test(media?.filename || "");

/**
 * First-page preview of a PDF (small thumbnail) so the right document can be
 * found at a glance. Falls back to the generic file icon for non-PDFs or on
 * any render error.
 */
export const PdfThumb = ({ media, className = "" }) => {
  const isPdf = looksLikePdf(media);
  const [src, setSrc] = useState(() => (media?.id ? thumbCache.get(media.id) || null : null));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!media?.id || !isPdf) return undefined;
    if (thumbCache.has(media.id)) { setSrc(thumbCache.get(media.id)); return undefined; }
    (async () => {
      try {
        const task = pdfjsLib.getDocument({ url: mediaSrc(media) });
        const pdf = await task.promise;
        const page = await pdf.getPage(1);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: 96 / base.width }); // ~96px wide
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        const url = canvas.toDataURL("image/jpeg", 0.72);
        thumbCache.set(media.id, url);
        try { pdf.destroy(); } catch (e) { /* ignore */ }
        if (alive) setSrc(url);
      } catch (e) {
        if (alive) setFailed(true);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media?.id, isPdf]);

  if (!media || !isPdf || failed) {
    return <FileText className={`h-5 w-5 text-primary shrink-0 ${className}`} />;
  }
  if (!src) {
    return <div className={`h-14 w-11 rounded-md border bg-muted animate-pulse shrink-0 ${className}`} data-testid="pdf-thumb-loading" />;
  }
  return (
    <img
      src={src}
      alt={media.filename || "PDF"}
      data-testid="pdf-thumb"
      className={`h-14 w-11 rounded-md border object-cover object-top bg-white shrink-0 ${className}`}
    />
  );
};
